import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../ui/dialog';
import { Trash2, RotateCcw, Loader2, AlertCircle, Link2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BUSY = new Set(['removing', 'restoring']);

const RemovedBankAccountsSection = ({ onChange, canManage = true, refreshKey = 0 }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [target, setTarget] = useState(null);
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [lastRestore, setLastRestore] = useState(null);
  const timer = useRef(null);

  const fetchRows = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/bank-accounts/removed`);
      setRows(r.data?.removed || []);
      return r.data?.removed || [];
    } catch {
      setRows([]);
      return [];
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchRows(); }, [fetchRows, refreshKey]);

  // Poll while any removal / restore is in flight.
  useEffect(() => {
    const busy = rows.some((r) => BUSY.has(r.status));
    if (!busy) return undefined;
    timer.current = setTimeout(async () => {
      const next = await fetchRows();
      const stillBusy = next.some((r) => BUSY.has(r.status));
      if (!stillBusy) {
        onChange?.();
        if (lastRestore && !next.some((r) => r.id === lastRestore.id)) {
          const st = await axios.get(`${API}/bank-accounts/removals/${lastRestore.id}`).then((x) => x.data).catch(() => null);
          if (st?.status === 'restored') {
            toast.success(
              st.needs_reconnect
                ? `${lastRestore.name} restored. Reconnect the bank from My Banks to pull the transactions you missed.`
                : `${lastRestore.name} restored with all its transactions, journals and documents.`,
              { duration: 9000 },
            );
          } else if (st?.status === 'restore_failed') {
            toast.error(`Restore failed: ${st.error || 'unknown error'}`);
          }
          setLastRestore(null);
        }
      }
    }, 3000);
    return () => clearTimeout(timer.current);
  }, [rows, fetchRows, onChange, lastRestore]);

  const restore = async () => {
    if (!target || !password) return;
    setSubmitting(true);
    try {
      const r = await axios.post(`${API}/bank-accounts/removals/${target.id}/restore`, { password });
      toast.success(r.data?.message || 'Restore started');
      setLastRestore({ id: target.id, name: target.account_snapshot?.bank_name || 'Bank account' });
      setTarget(null); setPassword('');
      fetchRows();
    } catch (e) {
      const d = e?.response?.data?.detail;
      if (d?.code === 'incorrect_password') toast.error('Incorrect password.');
      else toast.error(typeof d === 'string' ? d : d?.message || 'Failed to restore', { duration: 9000 });
    } finally {
      setSubmitting(false);
    }
  };

  if (loading || !rows.length) return null;

  return (
    <Card className="mt-6" data-testid="removed-banks-section">
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2">
          <Trash2 className="w-5 h-5 text-muted-foreground" />
          Removed bank accounts
        </CardTitle>
        <CardDescription>
          These accounts and everything posted through them were taken out of your books. Restore puts
          it all back — bank lines, journals, documents, schedules and payments.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="space-y-3">
          {rows.map((row) => {
            const a = row.account_snapshot || {};
            const s = row.summary || {};
            const busy = BUSY.has(row.status);
            return (
              <div key={row.id} className="flex items-center justify-between gap-3 p-3 border rounded-lg bg-muted/20"
                data-testid={`removed-bank-row-${row.bank_account_id}`} data-status={row.status}>
                <div className="flex items-center gap-3 flex-1 min-w-0">
                  <div className="w-10 h-10 rounded-lg bg-red-50 flex items-center justify-center flex-shrink-0">
                    <Trash2 className="w-5 h-5 text-red-400" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium truncate">
                      {a.bank_name || a.institution_name || 'Bank account'}
                      {a.mask && <span className="text-muted-foreground ml-1">····{a.mask}</span>}
                      {a.currency && <span className="text-muted-foreground ml-1 text-xs">{a.currency}</span>}
                    </div>
                    <div className="text-xs text-muted-foreground flex items-center gap-2 flex-wrap">
                      {row.removed_at && <span>Removed {new Date(row.removed_at).toLocaleDateString()}</span>}
                      {row.removed_by_email && <span>by {row.removed_by_email}</span>}
                      {row.provider && <Badge variant="outline" className="text-[10px] h-4 capitalize">was {row.provider}</Badge>}
                      <span>·</span>
                      <span>{s.feed_lines ?? 0} lines · {s.journals ?? 0} journals · {s.documents_total ?? 0} documents</span>
                      {row.reason && <span className="italic">“{row.reason}”</span>}
                    </div>
                    {row.status === 'failed' && <p className="text-xs text-red-600 mt-1">Removal failed: {row.error}</p>}
                    {row.status === 'restore_failed' && <p className="text-xs text-red-600 mt-1">Restore failed: {row.error}</p>}
                  </div>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  {busy ? (
                    <Badge variant="outline" className="gap-1 text-amber-700 border-amber-300" data-testid={`removed-bank-busy-${row.bank_account_id}`}>
                      <Loader2 className="w-3 h-3 animate-spin" /> {row.status === 'removing' ? 'Removing…' : 'Restoring…'}
                    </Badge>
                  ) : canManage && row.status === 'removed' ? (
                    <Button variant="outline" size="sm" onClick={() => { setTarget(row); setPassword(''); }}
                      className="text-[#108981] border-[#108981]/40 hover:bg-[#108981]/5"
                      data-testid={`restore-bank-${row.bank_account_id}`}>
                      <RotateCcw className="w-3.5 h-3.5 mr-1" /> Restore
                    </Button>
                  ) : null}
                </div>
              </div>
            );
          })}
        </div>
        <div className="mt-4 flex items-start gap-2 text-xs text-muted-foreground p-3 rounded-lg bg-slate-50 border">
          <AlertCircle className="w-4 h-4 mt-0.5 text-slate-500 flex-shrink-0" />
          <span>
            Connected accounts come back as manual — after restoring, reconnect the bank from My Banks so the
            transactions since removal are fetched. Any history the bank cannot reach can be filled with a statement upload.
          </span>
        </div>
      </CardContent>

      <Dialog open={!!target} onOpenChange={(o) => { if (!o) setTarget(null); }}>
        <DialogContent className="sm:max-w-md" data-testid="restore-bank-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <RotateCcw className="w-5 h-5 text-[#108981]" />
              Restore {target?.account_snapshot?.bank_name || 'bank account'}
            </DialogTitle>
            <DialogDescription>
              Puts back {target?.summary?.feed_lines ?? 0} bank lines, {target?.summary?.journals ?? 0} journal entries,
              {' '}{target?.summary?.documents_total ?? 0} documents and their schedules, and re-applies
              {' '}{target?.summary?.payments_unwound ?? 0} payment{(target?.summary?.payments_unwound ?? 0) === 1 ? '' : 's'} on existing invoices/bills.
              {target?.provider && (
                <span className="block mt-2 text-amber-700 inline-flex items-center gap-1">
                  <Link2 className="w-3.5 h-3.5" /> It was connected via {target.provider} — you will be asked to reconnect afterwards.
                </span>
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-1">
            <Label htmlFor="restore-password">Confirm your password</Label>
            <Input id="restore-password" type="password" autoComplete="current-password" value={password}
              onChange={(e) => setPassword(e.target.value)} data-testid="restore-password-input" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setTarget(null)} data-testid="restore-cancel-btn">Cancel</Button>
            <Button onClick={restore} disabled={!password || submitting} className="bg-[#108981] hover:bg-[#108981]/90"
              data-testid="restore-confirm-btn">
              {submitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} Restore bank account
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

export default RemovedBankAccountsSection;
